import React, { useCallback, useMemo, useState } from 'react'
import {
	Avatar,
	AutoComplete,
	Button,
	Card,
	Col,
	Dropdown,
	Grid,
	Input,
	Modal,
	Row,
	Select,
	Space,
	Table,
	Tabs,
	Tag,
	Typography,
	message,
} from 'antd'
import {
	CalendarOutlined,
	FileTextOutlined,
	SettingOutlined,
	UnorderedListOutlined,
} from '@ant-design/icons'
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table'
import type { FilterValue, SorterResult, TableCurrentDataSource } from 'antd/es/table/interface'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import GlobalHeader from '../../common/components/Header/GlobalHeader'
import PageHeader from '../../common/components/Header/PageHeader'
import { useThemeColors } from '../../common/ThemeContext'
import { leaveService } from '../../common/service/leave.service'
import { staffService } from '../../common/service/staff.service'
import AppSettingsTab from '../Settings/AppSettingsTab'
import LeaveSettings from './LeaveSettings'
import ManageBalance from './ManageBalance'
import PublicHolidays from './PublicHolidays'
import LeaveDetail from './LeaveDetail'

type LeaveStatus = 'Pending' | 'Approved' | 'Rejected' | 'Cancelled'

interface LeaveRecord {
	id: string
	leaveType?: string
	startDate?: string
	endDate?: string
	halfDayPeriod?: 'Morning' | 'Afternoon' | null
	days?: number
	reason?: string
	overallStatus?: LeaveStatus
	createdAt?: string
	updatedAt?: string
	requester?: {
		id?: string
		name?: string
		photoUrl?: string | null
		nameInEnglish?: string
		firstName?: string
		lastName?: string
	}
	requesterId?: string
	requesterName?: string
}

interface LeaveListApiData {
	items?: LeaveRecord[]
	data?: LeaveRecord[]
	total?: number
	records?: number
	pages?: number
	page?: number
}

const statusOptions: Array<{ value: LeaveStatus; label: string }> = [
	{ value: 'Pending', label: 'Pending' },
	{ value: 'Approved', label: 'Approved' },
	{ value: 'Rejected', label: 'Rejected' },
	{ value: 'Cancelled', label: 'Cancelled' },
]

const statusColor: Record<LeaveStatus, string> = {
	Pending: 'gold',
	Approved: 'green',
	Rejected: 'red',
	Cancelled: 'default',
}

const toArray = (payload: any): LeaveRecord[] => {
	if (Array.isArray(payload?.data)) {
		return payload.data
	}
	if (Array.isArray(payload?.items)) {
		return payload.items
	}
	if (Array.isArray(payload)) {
		return payload
	}
	if (Array.isArray(payload?.data?.items)) {
		return payload.data.items
	}
	if (Array.isArray(payload?.data?.data)) {
		return payload.data.data
	}
	return []
}

const LeaveManagement: React.FC = () => {
	const screens = Grid.useBreakpoint()
	const compactTable = !screens.xxl
	const { primary } = useThemeColors()
	const navigate = useNavigate()
	const [loading, setLoading] = useState<boolean>(false)
	const [data, setData] = useState<LeaveRecord[]>([])
	const [settingsOpen, setSettingsOpen] = useState(false)
	const [pagination, setPagination] = useState<TablePaginationConfig>({
		current: 1,
		pageSize: 10,
		total: 0,
	})
	const [status, setStatus] = useState<LeaveStatus | undefined>(undefined)
	const [leaveType, setLeaveType] = useState<string | undefined>(undefined)
	const [leaveTypeOptions, setLeaveTypeOptions] = useState<
		Array<{ value: string; label: string }>
	>([])
	const [requesterId, setRequesterId] = useState<string>('')
	const [requesterName, setRequesterName] = useState<string>('')
	const [staffOptions, setStaffOptions] = useState<
		{ value: string; label: string; staffId: string }[]
	>([])
	const [actionOpen, setActionOpen] = useState<boolean>(false)
	const [actionLoading, setActionLoading] = useState<boolean>(false)
	const [actionType, setActionType] = useState<'approve' | 'reject'>('approve')
	const [selectedLeave, setSelectedLeave] = useState<LeaveRecord | null>(null)
	const [actionComment, setActionComment] = useState<string>('')
	const [staffIdInput, setStaffIdInput] = useState<string>('')
	const [adminActionableById, setAdminActionableById] = useState<Record<string, boolean>>({})
	const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list')
	const [calendarYear, setCalendarYear] = useState<number>(dayjs().year())
	const [calendarMonth, setCalendarMonth] = useState<number>(dayjs().month() + 1)
	const [calendarLeaves, setCalendarLeaves] = useState<LeaveRecord[]>([])
	const [holidays, setHolidays] = useState<Array<{ date: string; name: string }>>([])
	const [selectedCalendarCell, setSelectedCalendarCell] = useState<{
		dateLabel: string
		holiday?: string
		leaves: LeaveRecord[]
	} | null>(null)
	const [detailOpen, setDetailOpen] = useState(false)
	const [detailLeaveId, setDetailLeaveId] = useState<string | null>(null)
	const [hubTab, setHubTab] = useState<
		'requests' | 'approval-workflow' | 'notifications-sla' | 'types' | 'balances' | 'holidays'
	>('requests')
	const fetchLeaves = useCallback(
		async (override?: Partial<TablePaginationConfig>) => {
			try {
				setLoading(true)
				const current = override?.current || pagination.current || 1
				const pageSize = override?.pageSize || pagination.pageSize || 10

				const response: any = await leaveService.fetchListing({
					status,
					leaveType: leaveType || undefined,
					requesterId: requesterId || undefined,
					page: current,
					limit: pageSize,
				})

				const apiData: LeaveListApiData = response?.data || response
				const items = toArray(apiData)
				const total =
					apiData?.total ||
					apiData?.records ||
					response?.total ||
					response?.records ||
					items.length

				setData(items)
				setPagination((prev) => ({
					...prev,
					current,
					pageSize,
					total,
				}))
			} catch (error: any) {
				message.error(error?.response?.data?.message || 'Failed to fetch leave requests')
			} finally {
				setLoading(false)
			}
		},
		[pagination.current, pagination.pageSize, requesterId, status, leaveType],
	)

	React.useEffect(() => {
		let mounted = true
		const loadLeaveTypeOptions = async () => {
			try {
				const res: any = await leaveService.getLeaveSettings()
				const types = res?.data?.types || res?.types || []
				const options = (Array.isArray(types) ? types : [])
					.filter((t: any) => t?.enabled !== false)
					.map((t: any) => ({
						value: String(t.key),
						label: String(t.label || t.key),
					}))
				if (mounted) {
					setLeaveTypeOptions(options)
				}
			} catch {
				if (mounted) {
					setLeaveTypeOptions([])
				}
			}
		}

		loadLeaveTypeOptions()

		return () => {
			mounted = false
		}
	}, [])

	React.useEffect(() => {
		fetchLeaves({ current: 1 })
	}, [status, leaveType])

	React.useEffect(() => {
		if (viewMode !== 'calendar') return
		leaveService
			.getHolidays(calendarYear)
			.then((r: any) => setHolidays(r?.data?.holidays || r?.holidays || []))
			.catch(() => setHolidays([]))
	}, [viewMode, calendarYear])

	const fetchCalendarLeaves = useCallback(async () => {
		try {
			const response: any = await leaveService.fetchListing({
				status,
				leaveType: leaveType || undefined,
				requesterId: requesterId || undefined,
				page: 1,
				limit: 500,
			})
			const apiData: LeaveListApiData = response?.data || response
			setCalendarLeaves(toArray(apiData))
		} catch {
			setCalendarLeaves([])
		}
	}, [status, leaveType, requesterId])

	React.useEffect(() => {
		if (viewMode !== 'calendar') return
		fetchCalendarLeaves()
	}, [viewMode, fetchCalendarLeaves])

	React.useEffect(() => {
		let cancelled = false
		const pendingRows = data.filter((r) => r.overallStatus === 'Pending')
		if (pendingRows.length === 0) {
			setAdminActionableById({})
			return
		}

		const checkAdminTurnSequentially = async () => {
			const nextMap: Record<string, boolean> = {}
			for (const row of pendingRows) {
				if (cancelled) return
				try {
					const detail: any = await leaveService.detail(row.id)
					const workflow =
						detail?.data?.approvalWorkflow || detail?.approvalWorkflow || []
					const nextPending = (workflow as any[]).find(
						(s) => String(s?.status || '').toLowerCase() === 'pending approval',
					)
					nextMap[row.id] =
						String(nextPending?.role || '') === 'Admin' ||
						Number(nextPending?.level) === 4
				} catch {
					nextMap[row.id] = false
				}
			}
			if (!cancelled) setAdminActionableById(nextMap)
		}

		checkAdminTurnSequentially()
		return () => {
			cancelled = true
		}
	}, [data])

	const columns: ColumnsType<LeaveRecord> = useMemo(
		() => [
			{ title: 'Leave ID', dataIndex: 'id', key: 'id', width: 220 },
			{
				title: 'Requester',
				key: 'requester',
				width: compactTable ? 180 : 240,
				ellipsis: compactTable,
				render: (record: LeaveRecord) => {
					const req = record.requester
					const name =
						record.requesterName ||
						req?.name ||
						req?.nameInEnglish ||
						`${req?.lastName || ''} ${req?.firstName || ''}`.trim() ||
						record.requesterId ||
						'-'
					if (!compactTable) return <span>{name}</span>
					return <Typography.Text ellipsis={{ tooltip: name }}>{name}</Typography.Text>
				},
			},
			{ title: 'Type', dataIndex: 'leaveType', key: 'leaveType', width: 130 },
			{
				title: 'Date Range',
				key: 'dateRange',
				width: compactTable ? 210 : 290,
				ellipsis: compactTable,
				render: (record: LeaveRecord) => {
					const start = record.startDate
						? dayjs(record.startDate).format('YYYY-MM-DD')
						: '-'
					const end = record.endDate ? dayjs(record.endDate).format('YYYY-MM-DD') : '-'
					const base = `${start} - ${end}`
					const text = record.halfDayPeriod ? `${base} (${record.halfDayPeriod})` : base
					if (!compactTable) return <span>{text}</span>
					return <Typography.Text ellipsis={{ tooltip: text }}>{text}</Typography.Text>
				},
			},
			{
				title: 'Days',
				dataIndex: 'days',
				key: 'days',
				width: 70,
				render: (value?: number) => (value != null ? value : '-'),
			},
			{
				title: 'Status',
				dataIndex: 'overallStatus',
				key: 'overallStatus',
				render: (value?: LeaveStatus) =>
					value ? <Tag color={statusColor[value]}>{value}</Tag> : <Tag>-</Tag>,
			},
			{
				title: 'Reason',
				dataIndex: 'reason',
				key: 'reason',
				width: compactTable ? 170 : 260,
				ellipsis: compactTable,
				render: (value?: string) => value || '-',
			},
			{
				title: 'Requested At',
				dataIndex: 'createdAt',
				key: 'createdAt',
				width: 150,
				render: (value?: string) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-'),
			},
			{
				title: 'Actions',
				key: 'actions',
				width: 300,
				render: (record: LeaveRecord) => {
					const isPending = record.overallStatus === 'Pending'
					const isAdminTurn = !!adminActionableById[record.id]
					const canAction = isPending && isAdminTurn
					return (
						<Space>
							<Button size='small' onClick={() => openDetailModal(record.id)}>
								Detail
							</Button>
							{isPending && !isAdminTurn ? (
								<Tag color='processing'>Waiting managers</Tag>
							) : null}
							<Button
								type='primary'
								size='small'
								disabled={!canAction}
								onClick={() => {
									setSelectedLeave(record)
									setActionType('approve')
									setActionComment('')
									setActionOpen(true)
								}}
							>
								Approve
							</Button>
							<Button
								danger
								size='small'
								disabled={!canAction}
								onClick={() => {
									setSelectedLeave(record)
									setActionType('reject')
									setActionComment('')
									setActionOpen(true)
								}}
							>
								Reject
							</Button>
						</Space>
					)
				},
			},
		],
		[navigate, compactTable, adminActionableById],
	)

	const handleSearch = () => {
		fetchLeaves({ current: 1 })
	}

	const handleClear = () => {
		setStatus(undefined)
		setLeaveType(undefined)
		setRequesterId('')
		setRequesterName('')
		setStaffOptions([])
		setStaffIdInput('')
		fetchLeaves({ current: 1, pageSize: pagination.pageSize })
	}

	const handleTableChange = (
		nextPagination: TablePaginationConfig,
		_filters: Record<string, FilterValue | null>,
		_sorter: SorterResult<LeaveRecord> | SorterResult<LeaveRecord>[],
		_extra: TableCurrentDataSource<LeaveRecord>,
	) => {
		fetchLeaves({
			current: nextPagination.current,
			pageSize: nextPagination.pageSize,
		})
	}

	const onConfirmAction = async () => {
		if (!selectedLeave?.id) {
			return
		}

		if (actionType === 'reject' && !actionComment.trim()) {
			message.warning('A reason is required when rejecting.')
			return
		}

		try {
			setActionLoading(true)
			if (actionType === 'approve') {
				await leaveService.approve(selectedLeave.id, actionComment || undefined)
				message.success('Leave request approved successfully')
			} else {
				await leaveService.reject(selectedLeave.id, actionComment || undefined)
				message.success('Leave request rejected successfully')
			}
			setActionOpen(false)
			setSelectedLeave(null)
			setActionComment('')
			fetchLeaves()
		} catch (error: any) {
			message.error(error?.response?.data?.message || 'Failed to process leave request')
		} finally {
			setActionLoading(false)
		}
	}

	const openDetailModal = (leaveId: string) => {
		setDetailLeaveId(leaveId)
		setDetailOpen(true)
	}

	const calendarDays = useMemo(() => {
		const first = dayjs(
			`${calendarYear}-${String(calendarMonth).padStart(2, '0')}-01`,
		)
		const daysInMonth = first.daysInMonth()
		const startOffset = (first.day() + 6) % 7
		const cells: Array<{
			day?: number
			holiday?: string
			leaveCount?: number
			leaves?: LeaveRecord[]
			dateLabel?: string
		}> = []
		for (let i = 0; i < startOffset; i++) cells.push({})
		for (let d = 1; d <= daysInMonth; d++) {
			const current = dayjs(
				`${calendarYear}-${String(calendarMonth).padStart(2, '0')}-${String(d).padStart(2, '0')}`,
			)
			const currentStr = current.format('YYYY-MM-DD')
			const dayLeaves = calendarLeaves.filter((l) => {
				if (!l.startDate || !l.endDate) return false
				return currentStr >= l.startDate && currentStr <= l.endDate
			})
			const holiday = holidays.find((h) => h.date === currentStr)?.name
			cells.push({
				day: d,
				holiday,
				leaveCount: dayLeaves.length,
				leaves: dayLeaves,
				dateLabel: current.format('DD MMM YYYY'),
			})
		}
		return cells
	}, [calendarYear, calendarMonth, calendarLeaves, holidays])

	const statusBadgeColor = (status?: string) => {
		switch ((status || '').toLowerCase()) {
			case 'approved':
				return 'green'
			case 'rejected':
				return 'red'
			case 'cancelled':
				return 'default'
			default:
				return 'gold'
		}
	}

	const requestsPanel = (
		<>
			{/* ── Filter Bar ──────────────────────────────────────────────── */}
			<div style={{ padding: '12px 24px 0' }}>
				<Card
					size='small'
					style={{ borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}
					styles={{ body: { padding: '12px 16px' } }}
				>
					<Row gutter={[12, 8]} align='middle'>
						<Col xs={24} md={5}>
							<Select
								allowClear
								placeholder='Filter by Status'
								value={status}
								onChange={(value) => setStatus(value)}
								options={statusOptions}
								style={{ width: '100%' }}
							/>
						</Col>
						<Col xs={24} md={5}>
							<Select
								allowClear
								placeholder='Filter by Leave Type'
								value={leaveType}
								onChange={(value) => setLeaveType(value)}
								options={leaveTypeOptions}
								style={{ width: '100%' }}
							/>
						</Col>
						<Col xs={24} md={5}>
							<AutoComplete
								allowClear
								placeholder='Search requester by name'
								value={requesterName}
								options={staffOptions}
								onSearch={async (text) => {
									setRequesterName(text)
									if (!text.trim()) {
										setStaffOptions([])
										setRequesterId('')
										return
									}
									try {
										const results = await staffService.autocomplete(text.trim())
										setStaffOptions(
											results.map((s) => ({
												value: s.name,
												label: s.nameKh
													? `${s.name}  (${s.nameKh})`
													: s.name,
												staffId: s.id,
											})),
										)
									} catch {
										setStaffOptions([])
									}
								}}
								onSelect={(_value, option: any) => {
									setRequesterName(option.value)
									setRequesterId(option.staffId)
									setStaffIdInput('')
								}}
								onClear={() => {
									setRequesterName('')
									setRequesterId('')
									setStaffOptions([])
								}}
								style={{ width: '100%' }}
							/>
						</Col>
						<Col xs={24} md={5}>
							<Input.Search
								placeholder='Search by Staff ID'
								value={staffIdInput}
								allowClear
								enterButton
								onChange={(e) => {
									const val = e.target.value
									setStaffIdInput(val)
									if (!val.trim()) {
										setRequesterId('')
									}
								}}
								onSearch={(val) => {
									const id = val.trim()
									if (!id) {
										setRequesterId('')
										return
									}
									setRequesterName('')
									setStaffOptions([])
									setRequesterId(id)
									fetchLeaves({ current: 1 })
								}}
								style={{ width: '100%' }}
							/>
						</Col>
						<Col xs={24} md={4}>
							<div
								style={{
									display: 'flex',
									justifyContent: 'flex-end',
								}}
							>
								<Space>
									<Button onClick={handleSearch}>Search</Button>
									<Button onClick={handleClear}>Reset</Button>
									<Space.Compact>
										<Button
											type={viewMode === 'list' ? 'primary' : 'default'}
											icon={<UnorderedListOutlined />}
											onClick={() => setViewMode('list')}
										/>
										<Button
											type={viewMode === 'calendar' ? 'primary' : 'default'}
											icon={<CalendarOutlined />}
											onClick={() => setViewMode('calendar')}
										/>
									</Space.Compact>
								</Space>
							</div>
						</Col>
					</Row>
				</Card>
			</div>

			{/* ── Main Content ───────────────────────────────────────────── */}
			<div style={{ padding: '8px 24px 24px' }}>
				{viewMode === 'list' ? (
					<Card
						style={{ borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}
						styles={{ body: { padding: 0 } }}
					>
						<Table<LeaveRecord>
							columns={columns}
							dataSource={data}
							rowKey={(record) => record.id}
							loading={loading}
							scroll={{ x: 1200 }}
							tableLayout={compactTable ? 'fixed' : 'auto'}
							pagination={{
								current: pagination.current,
								pageSize: pagination.pageSize,
								total: pagination.total,
								showSizeChanger: true,
								pageSizeOptions: ['10', '20', '50'],
							}}
							onChange={handleTableChange}
						/>
					</Card>
				) : (
					<Card
						title='Leave Calendar'
						style={{ borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}
					>
						<Row gutter={12} style={{ marginBottom: 12 }}>
							<Col>
								<Select
									value={calendarYear}
									onChange={setCalendarYear}
									style={{ width: 120 }}
								>
									{Array.from({ length: 7 }).map((_, i) => {
										const y = dayjs().year() - 2 + i
										return (
											<Select.Option key={y} value={y}>
												{y}
											</Select.Option>
										)
									})}
								</Select>
							</Col>
							<Col>
								<Select
									value={calendarMonth}
									onChange={setCalendarMonth}
									style={{ width: 140 }}
								>
									{Array.from({ length: 12 }).map((_, i) => (
										<Select.Option key={i + 1} value={i + 1}>
											{dayjs().month(i).format('MMMM')}
										</Select.Option>
									))}
								</Select>
							</Col>
							<Col>
								<Button
									onClick={() => {
										setCalendarYear(dayjs().year())
										setCalendarMonth(dayjs().month() + 1)
									}}
								>
									Today
								</Button>
							</Col>
						</Row>
						<div
							style={{
								display: 'grid',
								gridTemplateColumns: 'repeat(7, minmax(120px, 1fr))',
								gap: 8,
							}}
						>
							{calendarDays.map((c, idx) => (
								<div
									key={idx}
									style={{
										border: '1px solid #e5e7eb',
										borderRadius: 8,
										minHeight: 90,
										padding: 8,
										background: c.holiday ? '#fff7e6' : '#fff',
										cursor: c.day ? 'pointer' : 'default',
									}}
									onClick={() => {
										if (!c.day) return
										setSelectedCalendarCell({
											dateLabel: c.dateLabel || '',
											holiday: c.holiday,
											leaves: c.leaves || [],
										})
									}}
								>
									{c.day ? (
										<>
											<Typography.Text strong>{c.day}</Typography.Text>
											<div style={{ marginTop: 4 }}>
												{c.holiday ? <Tag color='gold'>{c.holiday}</Tag> : null}
												{(c.leaveCount || 0) > 0 ? (
													<Tag color='blue'>{c.leaveCount} leave</Tag>
												) : null}
											</div>
										</>
									) : null}
								</div>
							))}
						</div>
					</Card>
				)}
			</div>
		</>
	)

	return (
		<>
			<GlobalHeader />

			<PageHeader
				title='Leave Management Hub'
				subtitle='Unified leave requests, workflow, notifications, and settings'
				icon={<FileTextOutlined />}
			/>

			<div style={{ padding: '8px 24px 24px' }}>
				<Card style={{ borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
					<Tabs
						activeKey={hubTab}
						onChange={(k) => setHubTab(k as any)}
						items={[
							{ key: 'requests', label: 'Requests', children: requestsPanel },
							{
								key: 'approval-workflow',
								label: 'Approval Workflow',
								children: <AppSettingsTab mode='approval-workflow' />,
							},
							{
								key: 'notifications-sla',
								label: 'Notifications & SLA',
								children: <AppSettingsTab mode='notifications-sla' />,
							},
							{
								key: 'types',
								label: 'Types',
								children: <LeaveSettings embedded />,
							},
							{
								key: 'balances',
								label: 'Balances',
								children: <ManageBalance embedded />,
							},
							{
								key: 'holidays',
								label: 'Holidays',
								children: <PublicHolidays embedded />,
							},
						]}
					/>
				</Card>
			</div>

			<Modal
				open={!!selectedCalendarCell}
				onCancel={() => setSelectedCalendarCell(null)}
				footer={null}
				title={selectedCalendarCell?.dateLabel || 'Date Details'}
			>
				{selectedCalendarCell?.holiday ? (
					<div style={{ marginBottom: 12 }}>
						<Tag color='gold'>Holiday: {selectedCalendarCell.holiday}</Tag>
					</div>
				) : null}
				{selectedCalendarCell && selectedCalendarCell.leaves.length === 0 ? (
					<Typography.Text type='secondary'>
						No leave records on this day.
					</Typography.Text>
				) : null}
				<Space direction='vertical' style={{ width: '100%' }} size={10}>
					{selectedCalendarCell?.leaves.map((l) => (
						<div
							key={`${l.id}-${l.startDate}-${l.endDate}`}
							style={{
								border: '1px solid #e5e7eb',
								borderRadius: 8,
								padding: 10,
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'center',
								gap: 8,
							}}
						>
							<div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
								<Avatar
									size={36}
									src={l.requester?.photoUrl || undefined}
									style={{ backgroundColor: '#dbeafe', color: '#1d4ed8' }}
								>
									{(l.requester?.name || '?').charAt(0).toUpperCase()}
								</Avatar>
								<div>
									<Typography.Text strong>{l.leaveType || 'Leave'}</Typography.Text>
									<div style={{ fontSize: 12, color: '#64748b' }}>
										{l.requester?.name || '-'} | {l.startDate} - {l.endDate}
									</div>
									<div style={{ fontSize: 12, color: '#334155', marginTop: 4 }}>
										Reason: {l.reason || '-'}
									</div>
									<Tag color={statusBadgeColor(l.overallStatus)} style={{ marginTop: 6 }}>
										{l.overallStatus || 'Pending'}
									</Tag>
								</div>
							</div>
							<Button
								size='small'
								onClick={() => {
									setSelectedCalendarCell(null)
									openDetailModal(l.id)
								}}
							>
								Detail
							</Button>
						</div>
					))}
				</Space>
			</Modal>

			<Modal
				open={detailOpen}
				onCancel={() => {
					setDetailOpen(false)
					setDetailLeaveId(null)
				}}
				footer={null}
				title='Leave Detail'
				width={980}
				destroyOnHidden
			>
				{detailLeaveId ? <LeaveDetail leaveId={detailLeaveId} embedded /> : null}
			</Modal>

			<Modal
				open={actionOpen}
				onCancel={() => {
					setActionOpen(false)
					setActionComment('')
					setSelectedLeave(null)
				}}
				onOk={onConfirmAction}
				okButtonProps={{ loading: actionLoading }}
				title={actionType === 'approve' ? 'Approve Leave Request' : 'Reject Leave Request'}
			>
				<Typography.Text style={{ display: 'block', marginBottom: 8 }}>
					Leave ID: {selectedLeave?.id || '-'}
				</Typography.Text>
				<Input.TextArea
					rows={4}
					placeholder={
						actionType === 'reject'
							? 'Reason for rejection (required)'
							: 'Optional comment'
					}
					value={actionComment}
					onChange={(e) => setActionComment(e.target.value)}
				/>
			</Modal>
		</>
	)
}

export default LeaveManagement
